import { FC, Fragment, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Input,
	Radio,
	Row,
	Select,
	Skeleton,
	Table,
	Tabs,
	Typography,
} from 'antd';
import {
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow from '@/libs/antd/components/DataRow';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import {
	CreditApprovalPayload,
	getProspectCreditCheck,
	getSlikRobotikDetail,
	getSlikRobotikResult,
	submitCreditApproval,
} from '@/libs/fleet-api/services/prospect';
import formRule from '@/libs/antd/rules';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useApp } from '@/App';
import RejectModal, {
	useRejectModal,
} from '../../fbdh/customer-checking/RejectModal';
import NegativeModal, {
	useNegativeModal,
} from '../../fbdh/customer-checking/NegativeModal';
import fetcher from '@/libs/fleet-api/fetcher';
import { useSelectRejectCategory } from '@/libs/fleet-api/hooks/master-data';
import dayjs from 'dayjs';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import AnalisaSLIKFormRobotikCCDH from './AnalisaSLIKForm.robotik';

const FAQ_TITLE = {
	'dukcapil': 'DUKCAPIL',
	'dttot': 'Daftar Terduga Teroris dan Organisasi Teroris (DTTOT)',
	'dppspm': 'Daftar Pendanaan Proliferasi Senjata Pemusnah Massal (DPPSPM)',
	'online-betting': 'Daftar Pihak/Rekening Terkait Judi Online',
	'pep':
		'Analisa EDD (Enhanced Due Dilligent) High Risk & Politically Exposed Person (PEP)',
};

const MAP_ANALYSIS = {
	'dukcapil': 'dukcapilAnalysis',
	'dttot': 'dttotAnalysis',
	'dppspm': 'dppspmAnalysis',
	'online-betting': 'onlineBettingAnalysis',
	'pep': 'pepAnalysis',
};

export type CCDHAnalisaSLIKPageProps = {
	prospectId: string;
};

const CCDHAnalisaSLIKPage: FC<CCDHAnalisaSLIKPageProps> = ({
	prospectId = '943P2024110005',
}) => {
	const { navigate, notification } = useApp();
	const {
		openCustomerDetail,
		openProspectDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openPreviewModal,
		openManagementModal,
		openShareholderModal,
		openNegativeHistoryModal,
		openRejectHistoryModal,
	} = useModals();
	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectCreditCheck', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCreditCheck(id);
		},
	});

	const {
		mainInformation,
		customerBusinessDocuments,
		shareholderSlikInformation,
		managementSlikInformation,
		customerLegalDocuments,
		customerReferralDocuments,
		creditExternalChecks,
		approvalHistory,
		negativeHistoryAnalysis,
		rejectedHistoryAnalysis,
		slikFile,
		slikCheckingStatus,
		debtorSlikInformation,
		customerNegativeHistory,
		customerRejectionHistory,
		slikInquiryMetric,
	} = data?.data || {};

	const { form, value, fieldName } = useForm<CreditApprovalPayload>();

	const submitApprovalMutation = useMutation({
		mutationFn: submitCreditApproval,
	});

	const isSlikRobotik = slikInquiryMetric?.isRobotic;
	const isSlikRobotikFailed = slikInquiryMetric?.isRoboticFailed;
	const showSlikManual = !isSlikRobotik || isSlikRobotikFailed;

	// get result slik robotik
	const { data: slikRobotikResult, isLoading: isLoadingSlikRobotikResult } =
		useQuery({
			queryKey: ['getSlikRobotikResult', prospectId] as const,
			queryFn: ({ queryKey }) => {
				const [, id] = queryKey;
				return getSlikRobotikResult(id);
			},
			enabled: !!prospectId,
		});

	// get result detail robotik
	const { data: slikRobotikDetail } = useQuery({
		queryKey: ['getSlikRobotikDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getSlikRobotikDetail(id);
		},
		enabled: isSlikRobotik,
	});

	const [rejectModal, setRejectModal] = useRejectModal();
	const [negativeModal, setNegativeModal] = useNegativeModal();
	const [activeTab, setActiveTab] = useState<string>('Internal Checking');

	const [slikCheckActiveTab, setSlikCheckActiveTab] = useState<
		'manager' | 'shareholder'
	>('manager');

	const rejectCategoryOptions = useSelectRejectCategory();

	const hasFailedExternalChecking = creditExternalChecks?.some((check) => {
		// temporary disable dukcapil check for all WNA case, requested by Akbar MTF
		if (check.category === 'dukcapil') {
			return false;
		}
		return check.creditExternalQuestions.some((question) => {
			return question.message !== null;
		});
	});

	const approvalOptions = hasFailedExternalChecking
		? ['Return', 'Reject']
		: ['Approve', 'Return', 'Reject'];

	if (isError) return <GenericErrorMessage />;

	if (isLoading) return null;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments.forEach((document) => {
											documents.push({
												name: document.documentType,
												type: getDocumentType(document.file.name)!,
												url: document.file.url!,
											});
										});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					try {
						await submitApprovalMutation.mutateAsync({
							prospectId: mainInformation!.prospectId,
							notes: values.notes,
							approvalStatus: values.approvalStatus,
							rejectCategoryId: values.rejectCategoryId,
							returnTo: values.returnTo,
						});
						navigate('CCDHAnalisaSLIKListPage');
						notification.success({
							message: 'Success',
							description: 'Data has been saved',
						});
					} catch (error) {
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages?.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li key={msg}>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				<Tabs
					activeKey={activeTab}
					onChange={(key) => setActiveTab(key)}
					items={[
						{
							label: 'Internal Checking',
							key: 'Internal Checking',
							children: (
								<Fragment>
									<FleetTitle>Reject Customer History</FleetTitle>
									<Table
										scroll={{ x: 'max-content' }}
										style={{
											marginBottom: 20,
										}}
										bordered
										loading={isLoading}
										pagination={false}
										dataSource={
											customerRejectionHistory?.map((reject, key) => {
												return {
													key,
													...reject,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												render: (_, record) => record.no,
											},
											{
												title: 'Prospect ID',
												render: (_, record) => record.prospectId,
											},
											{
												title: 'Customer Name',
												render: (_, record) => record.customerName,
											},
											{
												title: 'Reject Status',
												align: 'center',
												render: (_, record) => record.rejectedStatus,
											},
											{
												title: 'Reject Date',
												align: 'center',
												render: (_, record) =>
													record.rejectedDate
														? dayjs(record.rejectedDate).format('DD/MM/YYYY')
														: '-',
											},
											{
												title: 'Rejected Process',
												align: 'center',
												render: (_, record) => record.rejectedProcess,
											},
											{
												title: 'Rejected By',
												align: 'center',
												render: (_, record) => record.rejectedBy,
											},
											{
												title: 'Detail Reject',
												align: 'center',
												render: (_, record) => (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															openRejectHistoryModal(record.rejectedId!);
														}}
													>
														<FileSearchOutlined />
													</a>
												),
											},
										]}
									/>

									<Row
										gutter={[40, 20]}
										style={{ marginBottom: 30 }}
									>
										<Col span={24}>
											<Form.Item
												label="Analisa Reject History"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<div style={{ fontSize: 16, marginTop: 10 }}>
													{rejectedHistoryAnalysis?.analysisResult}
												</div>
											</Form.Item>
										</Col>
									</Row>

									<FleetTitle>Negative Customer History</FleetTitle>
									<Table
										style={{
											marginBottom: 20,
										}}
										scroll={{ x: 'max-content' }}
										loading={isLoading}
										pagination={false}
										dataSource={
											customerNegativeHistory?.map((negative, key) => {
												return {
													key,
													...negative,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												render: (_, record) => record.no,
											},
											{
												title: 'Prospect ID',
												render: (_, record) => record.prospectId ?? '-',
											},
											{
												title: 'Customer Name',
												render: (_, record) => record.customerName,
											},
											{
												title: 'Negative Status',
												align: 'center',
												render: (_, record) =>
													record.negativeStatus
														? record.negativeStatus === 'Bad' ||
															record.negativeStatus === 'Warning'
															? 'Yes'
															: 'No'
														: '-',
											},
											{
												title: 'Type',
												align: 'center',
												render: (_, record) => (
													<span
														className={`negative-badge status-${record.negativeType?.toLowerCase()}`}
													>
														{record.negativeType}
													</span>
												),
											},
											{
												title: 'Negative Date',
												render: (_, record) =>
													record.negativeDate
														? dayjs(record.negativeDate).format('DD/MM/YYYY')
														: '-',
											},
											{
												title: 'Source',
												render: (_, record) => record.source,
											},
											{
												title: 'Active',
												align: 'center',
												render: (_, record) =>
													record.isActive === 'Active' ? 'Yes' : 'No',
											},
											{
												title: 'Detail Negative',
												align: 'center',
												render: (_, record) => (
													<a
														href="#"
														onClick={(e) => {
															e.preventDefault();
															openNegativeHistoryModal(record);
															// setNegativeModal(true);
														}}
													>
														<FileSearchOutlined />
													</a>
												),
											},
										]}
									/>
									<Row
										gutter={[40, 20]}
										style={{ marginBottom: 30 }}
									>
										<Col span={24}>
											<Form.Item
												label="Analisa Negative History"
												labelCol={{
													span: 6,
													style: {
														alignSelf: 'center',
													},
												}}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
												rules={[formRule.required()]}
											>
												<div style={{ fontSize: 16, marginTop: 10 }}>
													{negativeHistoryAnalysis?.analysisResult}
												</div>
											</Form.Item>
										</Col>
									</Row>
									<Button
										type="primary"
										loading={submitApprovalMutation.isPending}
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 60,
										}}
										onClick={() => {
											setActiveTab('SLIK Checking');
										}}
									>
										NEXT
									</Button>
								</Fragment>
							),
						},
						{
							label: 'SLIK Checking',
							key: 'SLIK Checking',
							children: (
								<Fragment>
									{slikCheckingStatus?.id === 1 ? (
										<Fragment>
											<Row gutter={[40, 20]}>
												<Col span={12}>
													<Form.Item label="View Dokumen CES">
														<Button
															type="link"
															onClick={() => {
																const cesDocument =
																	customerReferralDocuments?.find((doc) =>
																		doc.documentType.includes(
																			'Customer Executive Summary',
																		),
																	);

																if (!cesDocument) return;
																openPreviewModal(cesDocument.file);
															}}
														>
															Dokumen-CES.pdf
														</Button>
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item label="SLIK Result">
														<Select
															disabled
															value={mainInformation?.slikResult}
														/>
													</Form.Item>
												</Col>
											</Row>
											<Divider />
											<Tabs
												activeKey={slikCheckActiveTab}
												onChange={(key) => {
													setSlikCheckActiveTab(
														key as typeof slikCheckActiveTab,
													);
												}}
												items={[
													{
														key: 'manager',
														label: 'Pengurus',
														children: (
															<Table
																bordered
																style={{ marginBottom: 20 }}
																pagination={false}
																dataSource={
																	managementSlikInformation?.map(
																		(manager, key) => {
																			return {
																				key,
																				...manager,
																			};
																		},
																	) ?? []
																}
																columns={[
																	{
																		title: 'View Detail',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'View Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Upload Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<UploadOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Nama Pengurus',
																		width: 300,
																		render: (_, record) => record.name,
																	},
																	{
																		title: 'SLIK Result',
																		render: (_, record) => {
																			return (
																				<Fragment>
																					<Select
																						size="small"
																						style={{ width: 80 }}
																						disabled
																						value={record.slikResult}
																					/>
																				</Fragment>
																			);
																		},
																	},
																]}
															/>
														),
													},
													{
														key: 'shareholder',
														label: 'Pemegang Saham',
														children: (
															<Table
																bordered
																style={{ marginBottom: 20 }}
																pagination={false}
																dataSource={
																	shareholderSlikInformation?.map(
																		(shareholder, key) => {
																			return {
																				key,
																				...shareholder,
																			};
																		},
																	) ?? []
																}
																columns={[
																	{
																		title: 'View Detail',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'View Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<FileSearchOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Upload Ideb',
																		width: 110,
																		align: 'center',
																		render: () => {
																			return (
																				<a
																					href="#"
																					style={{ color: '#bbb' }}
																					onClick={(e) => {
																						e.preventDefault();
																					}}
																				>
																					<UploadOutlined />
																				</a>
																			);
																		},
																	},
																	{
																		title: 'Nama Pemegang Saham',
																		width: 300,
																		render: (_, record) => record.name,
																	},
																	{
																		title: 'SLIK Result',
																		render: (_, record) => {
																			return (
																				<Fragment>
																					<Select
																						size="small"
																						style={{ width: 80 }}
																						disabled
																						value={record.slikResult}
																					/>
																				</Fragment>
																			);
																		},
																	},
																]}
															/>
														),
													},
												]}
											/>
										</Fragment>
									) : (
										<Fragment>
											{isSlikRobotik && !isSlikRobotikFailed ? (
												<Fragment>
													{(slikRobotikDetail?.data ?? []).length > 0 ? (
														<Alert
															message="Prospect ini menggunakan SLIK Robotik"
															type="warning"
															showIcon
															className="custom-alert-info"
														/>
													) : (
														<Alert
															message="Prospect ini menggunakan SLIK Robotik, dan seluruh data SLIK tidak ditemukan"
															type="warning"
															showIcon
															className="custom-alert-info"
														/>
													)}
													{isLoadingSlikRobotikResult ? (
														<Skeleton active />
													) : (
														<AnalisaSLIKFormRobotikCCDH
															shareholderSlikInformation={
																shareholderSlikInformation ?? []
															}
															managementSlikInformation={
																managementSlikInformation ?? []
															}
															data={slikRobotikDetail?.data ?? []}
															result={slikRobotikResult?.data.details}
														/>
													)}
												</Fragment>
											) : (
												<Fragment>
													<Typography.Title level={3}>
														Credit Summary
													</Typography.Title>
													<Table
														style={{
															marginBottom: 20,
														}}
														scroll={{ x: 'max-content' }}
														loading={isLoading}
														pagination={false}
														dataSource={[{ key: 1 }]}
														columns={[
															{
																title: 'View Detail',
																width: 120,
																align: 'center',
																render: () => (
																	<a
																		style={{ display: 'block' }}
																		onClick={(e) => {
																			e.preventDefault();
																			openCustomerDetail(
																				mainInformation!.customerId,
																			);
																		}}
																	>
																		<FileSearchOutlined />
																	</a>
																),
															},
															{
																title: 'View Ideb',
																width: 120,
																align: 'center',
																render: () => (
																	<a
																		style={{ display: 'block' }}
																		onClick={(e) => {
																			e.preventDefault();
																			if (slikFile) {
																				openPreviewModal({
																					name: slikFile?.name,
																					url: slikFile?.url,
																					id: 1,
																				});
																			}
																		}}
																	>
																		<FileSearchOutlined />
																	</a>
																),
															},
															{
																title: 'Nama Debitur',
																render: () =>
																	debtorSlikInformation?.name ?? '-',
															},
															{
																title: 'SLIK Result',
																render: () =>
																	mainInformation?.slikResult
																		? 'Kol ' + mainInformation.slikResult
																		: '-',
															},
															{
																title: 'NPWP',
																render: () =>
																	debtorSlikInformation?.npwp ?? '-',
															},
															{
																title: 'Plafon',
																render: () => '-',
															},
															{
																title: 'Baki Debet',
																render: () => '-',
															},
															{
																title: 'Tunggakan Pokok',
																render: () => '-',
															},
														]}
													/>

													<Divider />

													<Tabs
														style={{
															marginBottom: 20,
														}}
														items={[
															{
																label: 'Pengurus',
																key: 'Pengurus',
																children: (
																	<Table
																		scroll={{ x: 'max-content' }}
																		loading={isLoading}
																		pagination={false}
																		dataSource={
																			managementSlikInformation?.map(
																				(manager, key) => {
																					return {
																						key,
																						...manager,
																					};
																				},
																			) ?? []
																		}
																		columns={[
																			{
																				title: 'View Detail',
																				width: 120,
																				align: 'center',
																				render: (_, record) => (
																					<a
																						style={{ display: 'block' }}
																						onClick={(e) => {
																							e.preventDefault();
																							openManagementModal(record!.id);
																						}}
																					>
																						<FileSearchOutlined />
																					</a>
																				),
																			},
																			{
																				title: 'View Ideb',
																				width: 120,
																				align: 'center',
																				render: (_, record) => {
																					if (!showSlikManual) {
																						return (
																							<span>
																								<FileSearchOutlined
																									style={{ color: '#bababa' }}
																								/>
																							</span>
																						);
																					}
																					return (
																						<a
																							style={{ display: 'block' }}
																							onClick={(e) => {
																								e.preventDefault();
																								if (record.idebFile) {
																									openPreviewModal(
																										record.idebFile,
																									);
																								}
																							}}
																						>
																							<FileSearchOutlined />
																						</a>
																					);
																				},
																			},
																			{
																				title: 'Nama Pengurus',
																				render: (_, record) => record.name,
																			},
																			{
																				title: 'SLIK Result',
																				render: (_, record) =>
																					record.slikResult
																						? 'Kol ' + record.slikResult
																						: '-',
																			},
																			// {
																			// 	title: 'KTP',
																			// 	render: (_, record) =>
																			// 		record.identityNumber ?? '-',
																			// },
																			// {
																			// 	title: 'Plafon',
																			// 	render: () => '-',
																			// },
																			// {
																			// 	title: 'Baki Debet',
																			// 	render: () => '-',
																			// },
																			// {
																			// 	title: 'Tunggakan Pokok',
																			// 	render: () => '-',
																			// },
																		]}
																	/>
																),
															},
															{
																label: 'Pemegang Saham',
																key: 'Pemegang Saham',
																children: (
																	<Table
																		scroll={{ x: 'max-content' }}
																		loading={isLoading}
																		pagination={false}
																		dataSource={
																			shareholderSlikInformation?.map(
																				(shareholder, key) => {
																					return {
																						key,
																						...shareholder,
																					};
																				},
																			) ?? []
																		}
																		columns={[
																			{
																				title: 'View Detail',
																				width: 120,
																				align: 'center',
																				render: (_, record) => (
																					<a
																						style={{ display: 'block' }}
																						onClick={(e) => {
																							e.preventDefault();
																							openShareholderModal(record.id);
																						}}
																					>
																						<FileSearchOutlined />
																					</a>
																				),
																			},
																			{
																				title: 'View Ideb',
																				width: 120,
																				align: 'center',
																				render: (_, record) => {
																					if (!showSlikManual) {
																						return (
																							<span>
																								<FileSearchOutlined
																									style={{ color: '#bababa' }}
																								/>
																							</span>
																						);
																					}
																					return (
																						<a
																							style={{ display: 'block' }}
																							onClick={(e) => {
																								e.preventDefault();
																								if (record.idebFile) {
																									openPreviewModal(
																										record.idebFile,
																									);
																								}
																							}}
																						>
																							<FileSearchOutlined />
																						</a>
																					);
																				},
																			},
																			{
																				title: 'Nama Pemegang Saham',
																				render: (_, record) => record.name,
																			},
																			{
																				title: 'SLIK Result',
																				render: (_, record) =>
																					record.slikResult
																						? 'Kol ' + record.slikResult
																						: '-',
																			},
																			// {
																			// 	title: 'KTP',
																			// 	render: (_, record) =>
																			// 		record.identityNumber ?? '-',
																			// },
																			// {
																			// 	title: 'Plafon',
																			// 	render: () => '-',
																			// },
																			// {
																			// 	title: 'Baki Debet',
																			// 	render: () => '-',
																			// },
																			// {
																			// 	title: 'Tunggakan Pokok',
																			// 	render: () => '-',
																			// },
																		]}
																	/>
																),
															},
														]}
													/>

													{/* <Button
										type="primary"
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 20,
											marginBottom: 20,
										}}
									>
										VIEW ALL DETAIL
									</Button> */}

													<Divider />

													<Row gutter={[40, 20]}>
														<Col span={24}>
															<Form.Item
																label="Catatan"
																labelCol={{
																	span: 6,
																	style: {
																		alignSelf: 'center',
																	},
																}}
																wrapperCol={{
																	style: {
																		marginLeft: -10,
																	},
																}}
															>
																<p>
																	{
																		approvalHistory?.[
																			approvalHistory?.length - 1
																		]?.approvalNote
																	}
																</p>
															</Form.Item>
														</Col>
													</Row>
												</Fragment>
											)}
										</Fragment>
									)}

									<Button
										type="primary"
										loading={submitApprovalMutation.isPending}
										style={{
											width: 200,
											display: 'block',
											marginLeft: 'auto',
											marginTop: 60,
										}}
										onClick={() => {
											if (slikCheckActiveTab === 'manager') {
												setSlikCheckActiveTab('shareholder');
											} else {
												setActiveTab('Eksternal Checking');
											}
										}}
									>
										NEXT
									</Button>
								</Fragment>
							),
						},
						{
							label: 'Eksternal Checking',
							key: 'Eksternal Checking',
							children: (
								<Fragment>
									<div style={{ marginBottom: 20 }}>
										{creditExternalChecks?.map((check, groupIndex) => {
											return (
												<div
													key={groupIndex}
													style={{ marginBottom: 20 }}
												>
													<FleetTitle>
														{FAQ_TITLE[
															check.category as keyof typeof FAQ_TITLE
														] ?? check.category}
													</FleetTitle>
													{check.creditExternalQuestions.map(
														({ question, answer, evidenceFile, message }) => {
															if (question.id === 11) {
																return (
																	<Fragment key={question.id}>
																		<Divider />
																		<Row gutter={[40, 20]}>
																			<Col span={24}>
																				<Form.Item
																					name={[
																						'answers',
																						question.id,
																						'evidenceFile',
																					]}
																					initialValue={question.id}
																					noStyle
																				/>
																				<Form.Item
																					label={question.value}
																					labelCol={{
																						span: 20,
																						style: {
																							whiteSpace: 'normal',
																						},
																					}}
																					wrapperCol={{
																						style: {
																							marginLeft: -20,
																						},
																					}}
																					rules={[formRule.required()]}
																					valuePropName="fileList"
																					getValueFromEvent={(e) =>
																						Array.isArray(e) ? e : e?.fileList
																					}
																				>
																					<div
																						style={{
																							fontSize: 16,
																							marginTop: 10,
																						}}
																					>
																						<a
																							href="#"
																							onClick={(e) => {
																								e.preventDefault();
																								openPreviewModal(evidenceFile);
																							}}
																						>
																							Evidence
																						</a>
																					</div>
																				</Form.Item>
																			</Col>
																		</Row>
																	</Fragment>
																);
															}
															return (
																<Fragment key={question.id}>
																	<Divider />
																	<Row gutter={[40, 20]}>
																		<Col span={18}>
																			<Form.Item
																				name={['answers', question.id, 'id']}
																				initialValue={question.id}
																				noStyle
																			/>
																			<Form.Item
																				name={[
																					'answers',
																					question.id,
																					'answer',
																				]}
																				label={question.value}
																				initialValue={answer.value}
																				labelCol={{
																					span: 16,
																					style: {
																						whiteSpace: 'normal',
																					},
																				}}
																			>
																				<Radio.Group
																					style={{ display: 'flex' }}
																				>
																					<Radio
																						disabled
																						value={'Ya'}
																						style={{ lineHeight: '45px' }}
																					>
																						Ya
																					</Radio>
																					<Radio
																						disabled
																						value={'Tidak'}
																						style={{ lineHeight: '45px' }}
																					>
																						Tidak
																					</Radio>
																				</Radio.Group>
																			</Form.Item>
																		</Col>
																		<Col span={6}>
																			<Form.Item
																				name={[
																					'answers',
																					question.id,
																					'evidenceFile',
																				]}
																				initialValue={question.id}
																				noStyle
																			/>
																			<Form.Item
																				rules={[formRule.required()]}
																				valuePropName="fileList"
																				getValueFromEvent={(e) =>
																					Array.isArray(e) ? e : e?.fileList
																				}
																			>
																				<div
																					style={{
																						fontSize: 16,
																						marginTop: 10,
																					}}
																				>
																					<a
																						href="#"
																						onClick={(e) => {
																							e.preventDefault();
																							openPreviewModal(evidenceFile);
																						}}
																					>
																						Evidence
																					</a>
																				</div>
																			</Form.Item>
																		</Col>
																	</Row>
																	{message && (
																		<Alert
																			message={message}
																			type="warning"
																			showIcon
																			className="custom-alert"
																		/>
																	)}
																</Fragment>
															);
														},
													)}
													<Divider />
													<Row gutter={[40, 20]}>
														<Col span={24}>
															<Form.Item
																name={MAP_ANALYSIS[check.category]}
																label={check.analysisBy}
																labelCol={{
																	span: 6,
																	style: {
																		alignSelf: 'center',
																	},
																}}
																wrapperCol={{
																	style: {
																		marginLeft: -10,
																	},
																}}
															>
																<div style={{ fontSize: 16, marginTop: 10 }}>
																	{check.analysisResult}
																</div>
															</Form.Item>
														</Col>
													</Row>
												</div>
											);
										})}
									</div>

									<FleetTitle>Approval History</FleetTitle>
									<Table
										bordered
										loading={isLoading}
										pagination={false}
										dataSource={
											approvalHistory?.map((approval, key) => {
												return {
													key,
													...approval,
												};
											}) ?? []
										}
										columns={[
											{
												title: 'No',
												render: (_, record) => record.no,
											},
											{
												title: 'Name',
												render: (_, record) => record.approvalBy,
											},
											{
												title: 'Date',
												render: (_, record) => record.approvalDate,
											},
											{
												title: 'Process',
												render: (_, record) => record.approvalProcess,
											},
											{
												title: 'Status',
												align: 'center',
												render: (_, record) => {
													return (
														<span
															className={`review-status ${record.approvalStatus.toLowerCase()}`}
														>
															{record.approvalStatus}
														</span>
													);
												},
											},
											{
												title: 'Approval Notes',
												render: (_, record) => record.approvalNote,
											},
										]}
										style={{ marginBottom: 20 }}
									/>
									<div style={{ marginBottom: 20 }}>
										<Divider />
										<Row gutter={[40, 20]}>
											<Col span={12}>
												<Form.Item
													name={fieldName('approvalStatus')}
													label="Approval Status"
													rules={[formRule.required()]}
												>
													<Select
														options={approvalOptions.map((option) => ({
															label: option,
															value: option,
														}))}
													/>
												</Form.Item>
											</Col>
											{value?.approvalStatus === 'Reject' && (
												<Col span={12}>
													<Form.Item
														name={fieldName('rejectCategoryId')}
														label="Reject Category"
														rules={[formRule.required()]}
													>
														<Select {...rejectCategoryOptions} />
													</Form.Item>
												</Col>
											)}
										</Row>
										<Divider />
										<Row gutter={[40, 20]}>
											<Col span={24}>
												<Form.Item
													name={fieldName('notes')}
													label="Approval Notes"
													labelCol={{
														span: 6,
														style: {
															alignSelf: 'center',
														},
													}}
													wrapperCol={{
														style: {
															marginLeft: -10,
														},
													}}
													rules={[formRule.required()]}
												>
													<Input.TextArea rows={4} />
												</Form.Item>
											</Col>
										</Row>
										<Divider />
										<Button
											type="primary"
											htmlType="submit"
											loading={submitApprovalMutation.isPending}
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
												marginTop: 60,
											}}
										>
											SAVE
										</Button>
									</div>
								</Fragment>
							),
						},
					]}
					style={{
						marginBottom: 20,
					}}
				/>
			</Form>

			<RejectModal
				modal={rejectModal}
				onClose={() => setRejectModal(false)}
			/>

			<NegativeModal
				modal={negativeModal}
				onClose={() => setNegativeModal(false)}
			/>
		</Fragment>
	);
};

export default CCDHAnalisaSLIKPage;
